/* eslint-disable no-alert */
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "@tecma/i18n";
import { Button, Divider, Icon, LazyImage, useDevice } from "@tecma/ds";

import ContactUsModal from "components/ContactUsModal/ContactUsModal";
import PageLoader from "components/PageLoader";
import paths from "routes/routesPaths";
import useCarousel from "hooks/useCarousel";
import useRooms from "hooks/useRooms";
import { useSwiperController } from "hooks/useSwiperController";
import { useFetchImagePaths } from "hooks/useFecthImagePaths";
import { useFeaturesDownload } from "hooks/useFeatiresDownload";
import { getApartmentDetails } from "services/Apartment";
import { useAppSelector } from "store/store.hooks";
import { roomsSelectors } from "store/slices/rooms/selectors";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { datesStateSelectors } from "store/slices/dates/selector";
import { environmentSelectors } from "store/slices/environment/selector";
import { ApartmentDetails } from "types/apartment";
import { getLocalizedDates } from "utils/dates";

import { normalizeText } from "utils/NormalazeData";
import { ContactSection } from "../../components/ContactSection";
import { ModalImageContainer } from "../../components/ModalImageContainer";
import { CardApartmentCarousel } from "../../components/CardApartment/CardApartmentCarousel";
import { ApartmentFeatures } from "../../components/ApartmentFeatures";
import { ApartmentInfo } from "../../components/ApartmentInfo";
import BannerMobile from "../../components/BannerMobile/BannerMobile";
import { PriceSection } from "../../components/PriceSection";
import PriceSidebar from "../../components/PriceSidebar";
import "./details-page.scss";

const DetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const device = useDevice();
  const navigate = useNavigate();
  const { handleSaveQuote } = useRooms();
  const projectInfo = useAppSelector(
    applicationStateSelectors.selectProjectInfo,
  );
  const displayName = useAppSelector(applicationStateSelectors.displayName);
  const rooms = useAppSelector(roomsSelectors.selectRooms);
  const environment = useAppSelector(environmentSelectors.environment);
  const [modalImages, setModalImages] = useState<string | string[]>("");
  const [openImageContainerModal, setOpenImageContainerModal] = useState(false);
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const { startDate, endDate } = useAppSelector(datesStateSelectors.datesState);

  const [apartmentDetails, setApartmentDetails] =
    useState<ApartmentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const swiperController = useSwiperController();
  const localeStartDate = getLocalizedDates(startDate);
  const localeEndDate = getLocalizedDates(endDate);
  const currentRoom = useMemo(() => {
    return rooms?.find((room) => room.id === params.id);
  }, [rooms]);
  const [showDownloadFeaturesButton, setShowDownloadFeaturesButton] =
    useState(true);
  const { checkIfShouldShowDownloadFeaturesButton, onClickSeeAllFeatures } =
    useFeaturesDownload(apartmentDetails?.code || "");

  const { price, model, building, plan } = currentRoom || {};

  const hasPrice = typeof price === "number";
  const sliderImagePath = `${
    environment?.REACT_APP_BUCKET_BASEURL
  }/initiatives/${encodeURI(`${displayName}`)}/global/img/slider/${
    building?.complex
  }/room/${model}/default/`;
  const roomPlanImage = `${
    environment?.REACT_APP_BUCKET_BASEURL
  }/initiatives/${encodeURI(`${displayName}`)}/planimetry/${
    building?.complex
  }/room/${plan}.png`;
  const apartmentPlanImage = `${
    environment?.REACT_APP_BUCKET_BASEURL
  }/initiatives/${encodeURI(`${displayName}`)}/planimetry/${
    building?.complex
  }/apartment/${apartmentDetails?.plan.name}.png`;
  const { imagePaths } = useFetchImagePaths({
    path: sliderImagePath,
  });

  const carouselProps = useCarousel(imagePaths.length);

  const toggleImageContainerModal = (
    e?: SyntheticEvent,
    images?: string | string[],
  ) => {
    e?.stopPropagation();
    if (images) {
      setModalImages(images);
    }
    setOpenImageContainerModal(!openImageContainerModal);
  };
  const toggleContactUsModal = () => {
    setIsContactUsModalOpen(!isContactUsModalOpen);
  };

  const fetchApartmentDetails = useCallback(
    async (apartmentId: string) => {
      setIsLoading(true);
      const response = await getApartmentDetails({
        projectId: projectInfo?.id || "",
        apartmentId,
        baseUri: environment.REACT_APP_ASSET_API_REST_URI,
      });
      if (!response) navigate(paths.spaces);
      setApartmentDetails(response);
      setIsLoading(false);
    },
    [projectInfo?.id, params.apartment, navigate],
  );

  useEffect(() => {
    if (currentRoom?.apartment) fetchApartmentDetails(currentRoom.apartment);
  }, [currentRoom]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  useEffect(() => {
    if (rooms?.length && !currentRoom) navigate(paths.spaces);
  }, [currentRoom, navigate, rooms]);

  useEffect(() => {
    (async () => {
      const shouldShow = await checkIfShouldShowDownloadFeaturesButton();
      setShowDownloadFeaturesButton(shouldShow);
    })();
  }, [apartmentDetails?.code]);

  if (isLoading || !currentRoom) return <PageLoader />;

  return (
    <div className='details-page'>
      <div className='card-details'>
        <div className='card-details-header'>
          <Link className='go-back-button' to={paths.spaces}>
            <Icon iconName='chevron-left' />
            <span>{t("details-page.go-back")}</span>
          </Link>
          <CardApartmentCarousel
            onClick={(e: SyntheticEvent) =>
              toggleImageContainerModal(e, imagePaths)
            }
            images={imagePaths}
            swiperController={swiperController}
            {...carouselProps}
          />
        </div>
        <div className='content'>
          <ApartmentInfo
            name={currentRoom.name}
            model={model || ""}
            building={currentRoom.building}
          />
          <div className='planimetry-container'>
            <h3>{t("details-page.room-features")}</h3>
            <div className='features-container'>
              <ApartmentFeatures
                features={{
                  surface: currentRoom.surface,
                  bathroom: currentRoom.bathroom,
                }}
              />
            </div>
            <Button
              onClick={(e) => toggleImageContainerModal(e, roomPlanImage)}
              fluid
              color='transparent'
              className='planimetry'
            >
              <LazyImage.Image src={roomPlanImage} />
              <Button
                iconName='zoom-in'
                onClick={(e) => toggleImageContainerModal(e, roomPlanImage)}
                outlined
                size='small'
                iconSize='small'
              />
            </Button>
          </div>
          <Divider />
          <div>
            <div className='planimetry-container'>
              <h3>{t("details-page.apartment-features")}</h3>
              <div className='features-container'>
                <ApartmentFeatures
                  features={{
                    rooms: apartmentDetails?.plan?.mainFeatures.rooms,
                    surface: apartmentDetails?.plan.surfaceArea.total,
                    floor: apartmentDetails?.floor,
                    buildingName: normalizeText(
                      apartmentDetails?.building?.name || "",
                    ),
                  }}
                />
                {showDownloadFeaturesButton && (
                  <Button
                    className='download-button'
                    iconName='download'
                    size='small'
                    outlined
                    onClick={onClickSeeAllFeatures}
                  >
                    {t("details-page.see-all-features")}
                  </Button>
                )}
              </div>
              <Button
                onClick={(e) =>
                  toggleImageContainerModal(e, apartmentPlanImage)
                }
                fluid
                color='transparent'
                className='planimetry'
              >
                <LazyImage.Image src={apartmentPlanImage} />
                <Button
                  iconName='zoom-in'
                  onClick={(e) =>
                    toggleImageContainerModal(e, apartmentPlanImage)
                  }
                  outlined
                  size='small'
                  iconSize='small'
                />
              </Button>
            </div>
          </div>
          <Divider />
          <ContactSection onClick={toggleContactUsModal} />
        </div>
        {device.type === "desktop" && hasPrice && (
          <PriceSidebar
            price={currentRoom.price}
            startDate={localeStartDate}
            endDate={localeEndDate}
            onButtonClick={() => handleSaveQuote(currentRoom)}
          />
        )}
        {device.type !== "desktop" ? (
          <BannerMobile
            price={currentRoom.price}
            onButtonClick={() => handleSaveQuote(currentRoom)}
            buttonLabel={t("card-rooms.highlighted-content.dream.firstButton")}
            startDate={localeStartDate}
            endDate={localeEndDate}
          />
        ) : (
          <div>{!hasPrice && <PriceSection price={currentRoom.price} />}</div>
        )}
      </div>
      <ModalImageContainer
        images={modalImages}
        isOpen={openImageContainerModal}
        onClose={() => toggleImageContainerModal()}
        swiperController={swiperController}
        {...carouselProps}
      />
      <ContactUsModal
        isOpen={isContactUsModalOpen}
        onClose={toggleContactUsModal}
      />
    </div>
  );
};

export default React.memo(DetailsPage);
